import styled from "styled-components";
import { ButtonProps } from "react-html-props";

type VariantType =
    | "blue"
    | "cyan"
    | "blue-empty"
    | "cyan-filled"
    | "light-cyan-filled"
    | "green"
    | "magenta"
    | "red"
    | "grey"
    | "transparent"
    | "checkbox"
    | "white"
    | "yellow";

type FontType = "default" | "poppins";
type WidthVariantType = "full";
type AnimationLevelType = "constrast" | "reduce-scale";

const Container = styled.button`
    display: flex;
    padding: 0.625rem 1rem;
    justify-content: center;
    align-items: center;
    gap: 0.48613rem;
    color: var(--Neutral-0, #fff);
    font-family: "Professor Marbles 3";
    font-size: 0.97225rem;
    font-style: normal;
    font-weight: 400;
    border-radius: 0.5rem;
    transition: 0.2s ease-in-out, outline 0s;

    &.blue {
        background: #195da5;

        &:disabled {
            background: var(--Neutral-400, #a1a1aa);
            cursor: not-allowed;
            --hover-scale: 1;
            --active-scale: 1;
        }
    }

    &.animated {
        --hover-scale: 1.05;
        --active-scale: 0.95;
    }

    svg {
        width: 1.5rem;
        height: 1.5rem;
    }

    &.cyan {
        color: #00a0df;
        outline: 2px solid #00a0df; // TODO: usar box-shadow inset ao invés de outline pra evitar overflow

        &:disabled {
            color: #a1a1aa;
            outline: 2px solid #a1a1aa;
            cursor: not-allowed;
            --hover-scale: 1;
            --active-scale: 1;
        }
    }

    &.transparent {
        padding: 0;
    }

    &.checkbox {
        position: relative;
        border-radius: 0.33331rem;
        background: rgba(25, 93, 165, 0.15);
        aspect-ratio: 1/1;
        /* width: 1.25rem;
        height: 1.25rem; */
        padding: 0;
        display: flex;
        justify-content: center;
        align-items: center;

        svg {
            width: 1.25rem;
            height: 1.25rem;
            color: #00a0df;
            transition: 0.2s;
        }

        &[aria-checked="false"] svg {
            transform: scale(0);
        }

        &[aria-checked="true"] svg {
            transform: scale(0.75);
        }
    }

    &.blue-empty {
        color: #195da5 !important;
        outline: 2px solid #195da5;
        background-color: #fff;
    }

    &.cyan-filled {
        color: white;
        background-color: #00a0df;
    }

    &.light-cyan-filled {
        color: white;
        background-color: #5ec3e6;
    }

    &.magenta {
        background-color: #e23a81;
        color: white;
    }
    &.red {
        background-color: #ff6464;
        color: white;
    }
    &.green {
        background-color: #50af4a;
        color: white;
    }
    &.grey {
        background-color: #71717a;
        color: white;
    }

    &.white {
        background-color: white;
        color: var(--dark-blue);
    }

    &.font-poppins {
        color: var(--Neutral-0, #fff);
        font-family: Poppins;
        font-size: 0.8125rem;
        font-style: normal;
        font-weight: 500;
        line-height: 1.05313rem; /* 129.617% */
        letter-spacing: -0.02438rem;
    }

    &.animation-constrast {
        &:hover {
            filter: contrast(1.2);
        }
    }

    &.animation-reduce-scale {
        --hover-scale: 1.01;
        --active-scale: 0.97;
    }

    &.width-full {
        width: 100%;
    }

    &.yellow {
        background-color: var(--yellow);
        color: #ffffff;
        text-shadow: 0 1px 2px rgba(0, 0, 0, 0.25);

        svg {
            color: #ffffff;
            filter: drop-shadow(0 1px 1px rgba(0, 0, 0, 0.25));
        }

        * {
            color: #ffffff;
        }
    }

    &:not(.blue-empty):not(.cyan):disabled {
        background: var(--Neutral-400, #a1a1aa);
        cursor: no-drop;
        --hover-scale: 1;
        --active-scale: 1;
    }
`;

export type MyButtonProps = {
    $variant: VariantType;
    $icon?: JSX.Element;
    $iconSide?: "left" | "right";
    $font?: FontType;
    $width?: WidthVariantType;
    $animationLevel?: AnimationLevelType[];
} & ButtonProps

export const Button = (
    props: MyButtonProps
) => {
    const classNames = ["animated"];

    if (props.$variant) classNames.push(props.$variant);
    if (props.className) classNames.push(props.className);
    if (props.$font) classNames.push(`font-${props.$font}`);
    if (props.$width) classNames.push(`width-${props.$width}`);
    if (Array.isArray(props.$animationLevel)) {
        const result = props.$animationLevel.map((d) => `animation-${d}`);
        classNames.push(...result);
    }

    return (
        <Container {...props} className={classNames.join(" ")}>
            {props.$iconSide !== "right" && props.$icon}
            {props.children}
            {props.$iconSide === "right" && props.$icon}
        </Container>
    );
};
